/**
 * Home: the Command Center. Money, the week against its targets, today's work
 * (yours, the team's, the AI employees'), what needs a human, where customers
 * come from, and what just happened. One call: GET /api/command/home.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  AlertTriangle, ArrowRight, BadgeDollarSign, Bot, CalendarDays, CheckCircle2, CircleDollarSign,
  Clock, CreditCard, Flag, Gauge, Hourglass, Inbox, Pencil, RefreshCw, Rocket, Sparkles, Target, TrendingUp, Users, Video,
} from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { Avatar, Skeleton, useToast } from "@/components/ui";
import { cn } from "@/lib/utils";
import {
  Card, Delta, Empty, PageHeader, Section, ShareBar, Spark, Stat, TickButton,
  dotClass, dueLabel, firstName, num, timeAgo, usd,
} from "@/features/command/ui";
import { QuickAdd } from "@/features/command/QuickAdd";
import { TargetsModal } from "@/features/command/TargetsModal";

interface Numbers { visits: number; visitors: number; signups: number; trials: number; paying: number; revenue: number; spend: number }
interface TaskCard {
  id: string; title: string; due_at: string | null; status: string; recurrence: unknown; agent_slug: string | null;
  source_name: string | null; source_color: string | null; contact_id: string | null; contact_name: string | null;
  goal_title: string | null; assignee_name: string | null;
}
interface HomeData {
  me: { id: string; name: string } | null;
  today: string;
  money: { mrr: number; paying: number; trials: number; trials_ending_7d: number; payment_issues: number; lifetime: number; free: number; beta: number; accounts: number };
  week: { start: string; this: Numbers; last: Numbers; targets: { visitors: number; signups: number; paying: number; revenue: number } };
  month: Numbers & { unattributed_signups: number };
  sources: Array<{ id: string; slug: string; name: string; kind: string; n30: Numbers }>;
  work: { mine: TaskCard[]; team: Array<{ id: string; name: string; image: string | null; open: number; overdue: number; today: number }> };
  moves: Array<{ id: string; kind: string; label: string; due_date: string; affiliate_id: string; name: string }>;
  ai: { available: boolean; waiting: Array<Record<string, unknown>>; working: number; done_today: number };
  alerts: Array<{ kind: string; tone: string; text: string; link: string }>;
  activity: Array<{ id: string; event: string; label: string; at: string; account_id: string | null; account_name: string | null; contact_id: string | null; contact_name: string | null; meta: Record<string, unknown> }>;
  goals: Array<{ id: string; title: string; due_date: string | null; tasks_total: number; tasks_done: number; source_name: string | null }>;
  series: Array<{ date: string; visitors: number; signups: number; revenue: number }>;
}
interface CalItem { id: string; type: string; title: string; date: string; start: string | null; done?: boolean; link?: string | null }

function greeting(): string {
  const h = new Date().getHours();
  return h < 5 ? "Working late" : h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
}

export function HomePage() {
  const toast = useToast();
  const { pathname } = useLocation();
  const [data, setData] = useState<HomeData | null>(null);
  const [editingTargets, setEditingTargets] = useState(false);
  const [brief, setBrief] = useState<{ text: string; via: string; at: string } | null>(null);
  const [week, setWeek] = useState<CalItem[] | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await api.get<HomeData>("/command/home");
      setData(d);
      const to = new Date(`${d.today}T12:00:00`);
      to.setDate(to.getDate() + 6);
      const cal = await api.get<{ items: CalItem[] }>("/command/calendar", { from: d.today, to: to.toISOString().slice(0, 10) });
      setWeek(cal.items);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't load the command center");
    }
  }, [toast]);

  useEffect(() => {
    void load();
    api.get<{ text: string; via: string; at: string }>("/command/brief").then(setBrief).catch(() => setBrief(null));
  }, [load]);

  const refreshBrief = async () => {
    setRefreshing(true);
    try { setBrief(await api.get("/command/brief", { refresh: 1 })); } catch { /* keep the old one */ } finally { setRefreshing(false); }
  };

  const toggleTask = async (t: TaskCard) => {
    try {
      await api.patch(`/tasks/${t.id}`, { status: t.status === "completed" ? "pending" : "completed" });
      void load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't update that task");
    }
  };

  const days = useMemo(() => {
    if (!data) return [];
    return Array.from({ length: 7 }).map((_, i) => {
      const d = new Date(`${data.today}T12:00:00`);
      d.setDate(d.getDate() + i);
      const key = d.toISOString().slice(0, 10);
      return { key, date: d, items: (week ?? []).filter((x) => x.date === key) };
    });
  }, [data, week]);

  if (!data) {
    return (
      <div className="space-y-5">
        <Skeleton className="h-16 w-80" />
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-28" />)}</div>
        <div className="grid gap-4 lg:grid-cols-3"><Skeleton className="h-96 lg:col-span-2" /><Skeleton className="h-96" /></div>
      </div>
    );
  }

  const w = data.week;
  const tgt = (v: number, t: number, money = false) => (t > 0 ? { value: v / t, label: `${money ? usd(v) : num(v)} of a ${money ? usd(t) : num(t)} goal` } : null);
  const late = data.work.mine.filter((t) => dueLabel(t.due_at).tone === "late");
  const todays = data.work.mine.filter((t) => ["today", "none"].includes(dueLabel(t.due_at).tone));
  const next = data.work.mine.filter((t) => ["soon"].includes(dueLabel(t.due_at).tone)).slice(0, 5);
  const bestSource = Math.max(1, ...data.sources.map((s) => s.n30.signups));

  return (
    <div className="space-y-5 pb-10">
      <PageHeader
        eyebrow={new Date(`${data.today}T12:00:00`).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
        title={<>{greeting()}{data.me?.name && data.me.id !== "dev-user" ? `, ${firstName(data.me.name)}` : ""}</>}
        actions={
          <Link to="/traffic" className="inline-flex h-9 items-center gap-2 rounded-xl border border-zinc-800 bg-zinc-900/60 px-3 text-sm text-zinc-300 hover:border-indigo-400/40 hover:text-zinc-100">
            <Rocket className="h-4 w-4 text-indigo-300" /> Traffic sources
          </Link>
        }
      />

      {/* The brief + the one input everything starts from */}
      <Card className="relative overflow-hidden p-4 sm:p-5">
        <div className="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full bg-indigo-500/15 blur-3xl" />
        <div className="relative flex items-start gap-3">
          <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl cc-brand-bg shadow-lg shadow-indigo-500/20">
            <Sparkles className="h-4 w-4 text-white" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-indigo-300/90">
              Today in one breath
              <button type="button" onClick={refreshBrief} title="Write it again" className="text-zinc-500 hover:text-zinc-200">
                <RefreshCw className={cn("h-3 w-3", refreshing && "animate-spin")} />
              </button>
            </div>
            {brief ? (
              <p className="mt-1 text-[15px] leading-relaxed text-zinc-100">{brief.text}</p>
            ) : (
              <Skeleton className="mt-2 h-10 w-full" />
            )}
          </div>
        </div>
        <QuickAdd className="relative mt-4" onAdded={load} />
      </Card>

      {/* Money */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat big tone="brand" icon={CircleDollarSign} label="Collecting" value={<>{usd(data.money.mrr)}<span className="text-base font-medium text-zinc-400">/mo</span></>} sub={`${num(data.money.paying)} paying customer${data.money.paying === 1 ? "" : "s"}`} to="/funnel" />
        <Stat icon={Hourglass} label="On trial" value={num(data.money.trials)} sub={data.money.trials_ending_7d ? `${data.money.trials_ending_7d} ending this week` : "none ending this week"} tone={data.money.trials_ending_7d ? "warn" : "default"} to="/accounts" />
        <Stat icon={TrendingUp} label="Earned, all time" value={usd(data.money.lifetime)} sub={`${num(data.money.accounts)} accounts, ${num(data.money.free)} free`} to="/funnel" />
        <Stat icon={CreditCard} label="Payment problems" value={num(data.money.payment_issues)} sub={data.money.payment_issues ? "card failing, act today" : "all payments going through"} tone={data.money.payment_issues ? "bad" : "good"} to="/accounts" />
      </div>

      <TargetsModal
        open={editingTargets}
        current={w.targets}
        onClose={() => setEditingTargets(false)}
        onSaved={(targets) => setData((d) => (d ? { ...d, week: { ...d.week, targets } } : d))}
      />

      {/* This week vs targets */}
      <div>
        <div className="mb-2 flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-zinc-200">
            This week <span className="font-normal text-zinc-500">vs last week and your goals</span>
            <button type="button" onClick={() => setEditingTargets(true)} title="Change the goals" className="rounded-md p-1 text-zinc-500 hover:bg-zinc-800 hover:text-zinc-200">
              <Pencil className="h-3.5 w-3.5" />
            </button>
          </h2>
          <Link to="/traffic?tab=website" className="text-xs text-indigo-300 hover:underline">Website numbers</Link>
        </div>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Stat icon={Users} label="Visitors" value={num(w.this.visitors)} now={w.this.visitors} prev={w.last.visitors} target={tgt(w.this.visitors, w.targets.visitors)} />
          <Stat icon={Rocket} label="Signups" value={num(w.this.signups)} now={w.this.signups} prev={w.last.signups} target={tgt(w.this.signups, w.targets.signups)} to="/accounts" />
          <Stat icon={BadgeDollarSign} label="New paying" value={num(w.this.paying)} now={w.this.paying} prev={w.last.paying} target={tgt(w.this.paying, w.targets.paying)} />
          <Stat icon={CircleDollarSign} label="Revenue" value={usd(w.this.revenue)} now={w.this.revenue} prev={w.last.revenue} target={tgt(w.this.revenue, w.targets.revenue, true)} />
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        {/* Left: the day */}
        <div className="space-y-4 lg:col-span-2">
          <Section
            title="Your day"
            icon={CheckCircle2}
            count={late.length + todays.length}
            action={<Link to="/work" className="text-xs text-indigo-300 hover:underline">All tasks</Link>}
            bodyClassName="p-0"
          >
            {late.length + todays.length + next.length === 0 ? (
              <Empty icon={CheckCircle2} title="Nothing on your plate today">Type a task above, or open Work to plan the week.</Empty>
            ) : (
              <div className="divide-y divide-zinc-800/60">
                {late.length > 0 && <Group label="Late" tone="late" />}
                {late.map((t) => <TaskLine key={t.id} t={t} onToggle={() => toggleTask(t)} from={pathname} />)}
                {todays.length > 0 && <Group label="Today" tone="today" />}
                {todays.map((t) => <TaskLine key={t.id} t={t} onToggle={() => toggleTask(t)} from={pathname} />)}
                {next.length > 0 && <Group label="Coming up" tone="soon" />}
                {next.map((t) => <TaskLine key={t.id} t={t} onToggle={() => toggleTask(t)} from={pathname} />)}
              </div>
            )}
          </Section>

          {/* The week at a glance */}
          <Section title="The next 7 days" icon={CalendarDays} action={<Link to="/work/calendar" className="text-xs text-indigo-300 hover:underline">Calendar</Link>}>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
              {days.map((d, i) => (
                <Link
                  key={d.key}
                  to="/work/calendar"
                  className={cn(
                    "min-h-28 rounded-xl border p-2 transition-colors hover:border-indigo-400/40",
                    i === 0 ? "border-indigo-400/40 bg-indigo-500/10" : "border-zinc-800 bg-zinc-900/40",
                  )}
                >
                  <div className="flex items-baseline justify-between">
                    <span className={cn("text-[11px] font-semibold uppercase tracking-wide", i === 0 ? "text-indigo-200" : "text-zinc-400")}>
                      {i === 0 ? "Today" : d.date.toLocaleDateString(undefined, { weekday: "short" })}
                    </span>
                    <span className="text-[11px] tabular-nums text-zinc-500">{d.date.getDate()}</span>
                  </div>
                  <div className="mt-1.5 space-y-1">
                    {d.items.slice(0, 4).map((it) => (
                      <div key={it.id} className={cn("truncate rounded-md px-1.5 py-0.5 text-[11px]",
                        it.type === "live_call" ? "bg-pink-500/15 text-pink-200" :
                        it.type === "event" ? "bg-amber-500/15 text-amber-200" :
                        it.type === "goal" ? "bg-violet-500/15 text-violet-200" :
                        it.type === "move" ? "bg-emerald-500/12 text-emerald-200" :
                        it.done ? "text-zinc-600 line-through" : "bg-zinc-800/70 text-zinc-300")}>
                        {it.type === "live_call" && <Video className="mr-1 inline h-3 w-3" />}
                        {it.start ? `${it.start} ` : ""}{it.title}
                      </div>
                    ))}
                    {d.items.length > 4 && <div className="px-1 text-[10px] text-zinc-500">+{d.items.length - 4} more</div>}
                  </div>
                </Link>
              ))}
            </div>
          </Section>

          {/* Where customers come from */}
          <Section title="Where customers came from, last 30 days" icon={Rocket} action={<Link to="/traffic" className="text-xs text-indigo-300 hover:underline">All sources</Link>}>
            {data.sources.length === 0 ? (
              <Empty icon={Rocket} title="No sourced signups yet">As people sign up through tagged links, ads and referral codes, the channels that work show up here.</Empty>
            ) : (
              <div className="space-y-3">
                {data.sources.slice(0, 7).map((s) => (
                  <Link key={s.id} to={`/traffic/${s.slug}`} className="grid grid-cols-[1fr_auto] items-center gap-x-4 gap-y-1 rounded-lg px-1 py-0.5 hover:bg-zinc-800/30 sm:grid-cols-[180px_1fr_auto]">
                    <span className="truncate text-sm text-zinc-200">{s.name}</span>
                    <ShareBar value={s.n30.signups} max={bestSource} className="col-span-2 order-last sm:order-none sm:col-span-1" />
                    <span className="text-right text-xs tabular-nums text-zinc-400">
                      <span className="font-semibold text-zinc-100">{num(s.n30.signups)}</span> signups
                      {s.n30.paying > 0 && <> · {num(s.n30.paying)} paying</>}
                      {s.n30.revenue > 0 && <> · {usd(s.n30.revenue)}</>}
                      {s.n30.visits > 0 && <span className="hidden md:inline"> · {num(s.n30.visits)} visits</span>}
                    </span>
                  </Link>
                ))}
                {data.month.unattributed_signups > 0 && (
                  <p className="pt-1 text-xs text-zinc-500">
                    {data.month.unattributed_signups} signup{data.month.unattributed_signups === 1 ? "" : "s"} this month came with no source. Tag every link you share (Traffic → a source → How we track it) to shrink that.
                  </p>
                )}
              </div>
            )}
          </Section>
        </div>

        {/* Right: what needs you */}
        <div className="space-y-4">
          <Section title="Needs you" icon={AlertTriangle} count={data.alerts.length + data.moves.length}>
            {data.alerts.length + data.moves.length === 0 ? (
              <Empty icon={CheckCircle2} title="All clear">No failing payments, ending trials, unanswered messages or affiliate follow-ups.</Empty>
            ) : (
              <div className="space-y-2">
                {data.alerts.map((a, i) => (
                  <Link key={i} to={a.link} className={cn(
                    "flex items-start gap-2 rounded-xl border px-3 py-2 text-sm transition-colors hover:brightness-110",
                    a.tone === "danger" ? "border-rose-500/30 bg-rose-500/10 text-rose-100" :
                    a.tone === "warning" ? "border-amber-500/30 bg-amber-500/10 text-amber-100" :
                    a.tone === "brand" ? "border-indigo-400/30 bg-indigo-500/10 text-indigo-100" :
                    "border-zinc-700 bg-zinc-800/40 text-zinc-200",
                  )}>
                    {a.kind === "ai" ? <Bot className="mt-0.5 h-4 w-4 shrink-0" /> : a.kind === "inbox" ? <Inbox className="mt-0.5 h-4 w-4 shrink-0" /> : a.kind === "trial" ? <Clock className="mt-0.5 h-4 w-4 shrink-0" /> : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />}
                    <span className="min-w-0 flex-1">{a.text}</span>
                  </Link>
                ))}
                {data.moves.slice(0, 6).map((m) => (
                  <Link key={m.id} to={`/affiliates/${m.affiliate_id}`} state={{ from: pathname }} className="flex items-center gap-2 rounded-xl border border-emerald-500/25 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-100 hover:brightness-110">
                    <BadgeDollarSign className="h-4 w-4 shrink-0" />
                    <span className="min-w-0 flex-1 truncate">{m.label}: <span className="font-semibold">{m.name}</span></span>
                    <ArrowRight className="h-3.5 w-3.5 shrink-0 opacity-70" />
                  </Link>
                ))}
              </div>
            )}
          </Section>

          <Section title="Goals" icon={Target} action={<Link to="/work/goals" className="text-xs text-indigo-300 hover:underline">All goals</Link>}>
            {data.goals.length === 0 ? (
              <Empty icon={Flag} title="No goals yet">Type something like "VSL live by Nov 15" in the box above.</Empty>
            ) : (
              <div className="space-y-3">
                {data.goals.map((g) => {
                  const d = dueLabel(g.due_date ? `${g.due_date}T12:00:00` : null);
                  const p = g.tasks_total ? g.tasks_done / g.tasks_total : 0;
                  return (
                    <Link key={g.id} to="/work/goals" className="block rounded-lg px-1 py-0.5 hover:bg-zinc-800/30">
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate text-sm text-zinc-200">{g.title}</span>
                        <span className={cn("shrink-0 text-[11px]", d.tone === "late" ? "text-rose-300" : d.tone === "today" || d.tone === "soon" ? "text-amber-300" : "text-zinc-500")}>{d.text}</span>
                      </div>
                      <div className="mt-1.5 flex items-center gap-2">
                        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-zinc-800"><div className={cn("h-full rounded-full", p >= 1 ? "bg-emerald-400" : "cc-brand-bg")} style={{ width: `${Math.max(p > 0 ? 4 : 0, p * 100)}%` }} /></div>
                        <span className="text-[10px] tabular-nums text-zinc-500">{g.tasks_done}/{g.tasks_total}</span>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </Section>

          <Section title="Team" icon={Users} action={<Link to="/team" className="text-xs text-indigo-300 hover:underline">Team</Link>}>
            <div className="space-y-2.5">
              {data.work.team.map((m) => (
                <div key={m.id} className="flex items-center gap-2.5">
                  <Avatar name={m.name} src={m.image} size="sm" />
                  <span className="min-w-0 flex-1 truncate text-sm text-zinc-200">{firstName(m.name)}</span>
                  <span className="text-xs tabular-nums text-zinc-400">{m.open} open</span>
                  {m.overdue > 0 && <span className="text-xs tabular-nums text-rose-300">{m.overdue} late</span>}
                </div>
              ))}
              <div className="flex items-center gap-2.5 border-t border-zinc-800/60 pt-2.5">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-indigo-500/15 ring-1 ring-indigo-400/30"><Bot className="h-3.5 w-3.5 text-indigo-300" /></div>
                <span className="min-w-0 flex-1 text-sm text-zinc-200">AI employees</span>
                <span className="text-xs text-zinc-400">
                  {data.ai.available ? <>{data.ai.working} working · {data.ai.done_today} done today</> : "not connected here"}
                </span>
              </div>
            </div>
          </Section>

          <Section title="Last 30 days" icon={Gauge}>
            <div className="space-y-3">
              <TrendRow label="Signups" values={data.series.map((d) => d.signups)} total={num(data.month.signups)} />
              <TrendRow label="Revenue" values={data.series.map((d) => d.revenue)} total={usd(data.month.revenue)} color="#ff6488" />
              <TrendRow label="Visitors" values={data.series.map((d) => d.visitors)} total={num(data.month.visitors)} color="#34d399" />
            </div>
          </Section>

          <Section title="Just happened" icon={Clock} bodyClassName="p-0">
            <div className="divide-y divide-zinc-800/60">
              {data.activity.map((a) => (
                <Link
                  key={a.id}
                  to={a.contact_id ? `/contacts/${a.contact_id}` : a.account_id ? `/accounts/${a.account_id}` : "/accounts"}
                  state={{ from: pathname }}
                  className="flex items-center gap-2 px-4 py-2 text-sm hover:bg-zinc-800/30"
                >
                  <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", a.event === "payment_succeeded" ? "bg-emerald-400" : a.event === "account_created" ? "bg-indigo-400" : a.event === "payment_failed" || a.event === "subscription_canceled" ? "bg-rose-400" : "bg-zinc-500")} />
                  <span className="min-w-0 flex-1 truncate text-zinc-300">
                    <span className="font-medium text-zinc-100">{a.contact_name || a.account_name || "Someone"}</span> {a.label}
                    {a.event === "payment_succeeded" && a.meta?.amountUsd ? <span className="text-emerald-300"> {usd(Number(a.meta.amountUsd), { cents: true })}</span> : null}
                  </span>
                  <span className="shrink-0 text-[11px] text-zinc-500">{timeAgo(a.at)}</span>
                </Link>
              ))}
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
}

function Group({ label, tone }: { label: string; tone: "late" | "today" | "soon" }) {
  return (
    <div className={cn("px-4 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wider", tone === "late" ? "text-rose-300" : tone === "today" ? "text-indigo-300" : "text-zinc-500")}>
      {label}
    </div>
  );
}

function TaskLine({ t, onToggle, from }: { t: TaskCard; onToggle: () => void; from: string }) {
  const due = dueLabel(t.due_at);
  return (
    <div className="group flex items-center gap-3 px-4 py-2.5 hover:bg-zinc-800/25">
      <TickButton done={t.status === "completed"} onToggle={onToggle} />
      <Link to="/work" className="min-w-0 flex-1">
        <div className={cn("truncate text-sm", t.status === "completed" ? "text-zinc-500 line-through" : "text-zinc-100")}>{t.title}</div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-zinc-500">
          {t.source_name && <span className="inline-flex items-center gap-1"><span className={cn("h-1.5 w-1.5 rounded-full", dotClass(t.source_color))} />{t.source_name}</span>}
          {t.goal_title && <span className="inline-flex items-center gap-1"><Flag className="h-3 w-3" />{t.goal_title}</span>}
          {t.agent_slug && <span className="inline-flex items-center gap-1 text-indigo-300"><Bot className="h-3 w-3" />AI does it</span>}
          {t.recurrence ? <span className="inline-flex items-center gap-1"><RefreshCw className="h-3 w-3" />repeats</span> : null}
        </div>
      </Link>
      {t.contact_id && t.contact_name && (
        <Link to={`/contacts/${t.contact_id}`} state={{ from }} className="hidden max-w-36 truncate text-xs text-zinc-400 hover:text-zinc-100 sm:block">{t.contact_name}</Link>
      )}
      <span className={cn("shrink-0 text-xs tabular-nums", due.tone === "late" ? "text-rose-300" : due.tone === "today" ? "text-indigo-200" : "text-zinc-500")}>{due.text}</span>
    </div>
  );
}

function TrendRow({ label, values, total, color }: { label: string; values: number[]; total: string; color?: string }) {
  const half = Math.floor(values.length / 2);
  const a = values.slice(0, half).reduce((s, v) => s + v, 0);
  const b = values.slice(half).reduce((s, v) => s + v, 0);
  return (
    <div className="flex items-center gap-3">
      <div className="w-20 shrink-0">
        <div className="text-[11px] text-zinc-500">{label}</div>
        <div className="text-sm font-semibold tabular-nums text-zinc-100">{total}</div>
      </div>
      <Spark values={values} color={color} className="flex-1" />
      <Delta now={b} prev={a} className="w-10 justify-end" />
    </div>
  );
}
